import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Save, CheckCircle } from 'lucide-react';
import * as Icons from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const DSAPartnerProgramSupportTrainingPage = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('cards'); // 'cards' or 'list'

    // Data States
    const [supportCards, setSupportCards] = useState([]);
    const [developmentItems, setDevelopmentItems] = useState([]);

    // Settings States
    const [supportSettings, setSupportSettings] = useState({
        section_title: '',
        section_subtitle: '',
        cta_text: '',
        cta_link: ''
    });
    const [developmentSettings, setDevelopmentSettings] = useState({
        section_title: '',
        section_subtitle: '',
        cta_text: '',
        cta_link: ''
    });

    const [savingSettings, setSavingSettings] = useState(false);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        description: '',
        icon_name: 'DocumentTextIcon',
        gradient_from: 'blue-500',
        gradient_to: 'cyan-500',
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, type: null });

    const supportLoanType = 'dsa_support_training';
    const developmentLoanType = 'dsa_ongoing_development';

    // Available Icons for selection
    const iconOptions = [
        'AcademicCapIcon',
        'UserGroupIcon',
        'DocumentTextIcon',
        'CheckCircleIcon',
        'ChartBarIcon',
        'LightBulbIcon',
        'PresentationChartLineIcon'
    ];

    // Color Options (Gradients)
    const colorOptions = [
        { label: 'Purple to Pink', from: 'purple-500', to: 'pink-600' },
        { label: 'Blue to Cyan', from: 'blue-500', to: 'cyan-600' },
        { label: 'Orange to Red', from: 'orange-500', to: 'red-600' },
        { label: 'Green to Emerald', from: 'green-500', to: 'emerald-600' },
        { label: 'Indigo to Purple', from: 'indigo-500', to: 'purple-600' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [
                supportSettingsRes,
                supportCardsRes,
                devSettingsRes,
                devItemsRes
            ] = await Promise.all([
                axios.get(`/api/admin/key-benefits/settings/${supportLoanType}`),
                axios.get(`/api/admin/key-benefits/${supportLoanType}`),
                axios.get(`/api/admin/key-benefits/settings/${developmentLoanType}`),
                axios.get(`/api/admin/key-benefits/${developmentLoanType}`)
            ]);

            if (supportSettingsRes.data.success) setSupportSettings(supportSettingsRes.data.data || {});
            if (supportCardsRes.data.success) setSupportCards(supportCardsRes.data.data || []);
            if (devSettingsRes.data.success) setDevelopmentSettings(devSettingsRes.data.data || {});
            if (devItemsRes.data.success) setDevelopmentItems(devItemsRes.data.data || []);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handlers
    const handleSettingsChange = (e, type) => {
        const { name, value } = e.target;
        if (type === 'support') {
            setSupportSettings(prev => ({ ...prev, [name]: value }));
        } else {
            setDevelopmentSettings(prev => ({ ...prev, [name]: value }));
        }
    };

    const saveSettings = async (e, type) => {
        e.preventDefault();
        setSavingSettings(true);
        const loanType = type === 'support' ? supportLoanType : developmentLoanType;
        const settings = type === 'support' ? supportSettings : developmentSettings;

        try {
            await axios.put(`/api/admin/key-benefits/settings/${loanType}`, settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Item Handlers
    const handleAddNew = () => {
        setEditingItem(null);
        const currentList = activeTab === 'cards' ? supportCards : developmentItems;
        setModalData({
            title: '',
            description: '',
            icon_name: activeTab === 'cards' ? 'AcademicCapIcon' : 'CheckCircleIcon',
            gradient_from: 'blue-500',
            gradient_to: 'cyan-500',
            display_order: currentList.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setModalData({
            title: item.title,
            description: item.description || '',
            icon_name: item.icon_name,
            gradient_from: item.gradient_from || 'blue-500',
            gradient_to: item.gradient_to || 'cyan-500',
            display_order: item.display_order,
            is_active: item.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Item deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting item:', error);
            toast.error('Failed to delete item');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const loanType = activeTab === 'cards' ? supportLoanType : developmentLoanType;
            const payload = { ...modalData, loan_type: loanType };

            if (editingItem) {
                await axios.put(`/api/admin/key-benefits/${editingItem.id}`, payload);
                toast.success('Item updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Item added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving item:', error);
            toast.error('Failed to save item');
        }
    };

    // Render Icon Helper
    const renderIcon = (iconName, className = "h-6 w-6") => {
        const IconComponent = Icons[iconName] || Icons.DocumentTextIcon;
        return <IconComponent className={className} />;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Support & Training</h1>
                    <p className="text-slate-500">Manage support resources and development programs</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 border-b border-slate-200">
                <button
                    onClick={() => setActiveTab('cards')}
                    className={`pb-4 px-4 font-medium transition-colors relative ${activeTab === 'cards'
                            ? 'text-primary-600 border-b-2 border-primary-600'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                >
                    Support Cards
                </button>
                <button
                    onClick={() => setActiveTab('list')}
                    className={`pb-4 px-4 font-medium transition-colors relative ${activeTab === 'list'
                            ? 'text-primary-600 border-b-2 border-primary-600'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                >
                    Development List
                </button>
            </div>

            {activeTab === 'cards' ? (
                /* Support Cards Section */
                <div className="space-y-8 animate-fadeIn">
                    {/* Settings */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                        <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                        <form onSubmit={(e) => saveSettings(e, 'support')} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    name="section_title"
                                    value={supportSettings.section_title}
                                    onChange={(e) => handleSettingsChange(e, 'support')}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle</label>
                                <input
                                    type="text"
                                    name="section_subtitle"
                                    value={supportSettings.section_subtitle}
                                    onChange={(e) => handleSettingsChange(e, 'support')}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button type="submit" disabled={savingSettings} className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700">
                                    <Save size={18} /> {savingSettings ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Cards List */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-800">Support Cards</h2>
                            <button onClick={handleAddNew} className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700">
                                <Plus size={18} /> Add New Card
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {supportCards.map((card) => (
                                <div key={card.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${card.gradient_from} to-${card.gradient_to} flex items-center justify-center mb-4 text-white`}>
                                        {renderIcon(card.icon_name)}
                                    </div>
                                    <h3 className="font-bold text-slate-800 mb-2">{card.title}</h3>
                                    <p className="text-sm text-slate-500 mb-4 line-clamp-3">{card.description}</p>
                                    <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                                        <span className={`text-xs font-medium px-2 py-1 rounded-lg ${card.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {card.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleEdit(card)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                            <button onClick={() => handleDelete(card.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                /* Development List Section */
                <div className="space-y-8 animate-fadeIn">
                    {/* Settings */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                        <h2 className="text-lg font-bold text-slate-800 mb-4">List Section Settings</h2>
                        <form onSubmit={(e) => saveSettings(e, 'development')} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    name="section_title"
                                    value={developmentSettings.section_title}
                                    onChange={(e) => handleSettingsChange(e, 'development')}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button type="submit" disabled={savingSettings} className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700">
                                    <Save size={18} /> {savingSettings ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* List Items */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-800">List Items</h2>
                            <button onClick={handleAddNew} className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700">
                                <Plus size={18} /> Add New Item
                            </button>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                            {developmentItems.map((item) => (
                                <div key={item.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="text-primary-600">
                                            {renderIcon(item.icon_name)}
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-slate-800">{item.title}</h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className={`text-xs font-medium px-2 py-1 rounded-lg ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {item.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleEdit(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                            <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Title</label>
                                <input
                                    type="text"
                                    value={modalData.title}
                                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    required
                                />
                            </div>

                            {activeTab === 'cards' && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Description</label>
                                    <textarea
                                        value={modalData.description}
                                        onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                        rows={3}
                                        required
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Icon</label>
                                    <select
                                        value={modalData.icon_name}
                                        onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    >
                                        {iconOptions.map(icon => (
                                            <option key={icon} value={icon}>{icon}</option>
                                        ))}
                                    </select>
                                </div>
                                {activeTab === 'cards' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Color Gradient</label>
                                        <select
                                            value={modalData.gradient_from}
                                            onChange={(e) => {
                                                const selected = colorOptions.find(c => c.from === e.target.value);
                                                if (selected) {
                                                    setModalData({
                                                        ...modalData,
                                                        gradient_from: selected.from,
                                                        gradient_to: selected.to
                                                    });
                                                }
                                            }}
                                            className="w-full px-4 py-2 border rounded-xl"
                                        >
                                            {colorOptions.map(c => (
                                                <option key={c.from} value={c.from}>{c.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={modalData.display_order}
                                        onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    />
                                </div>
                                <div className="flex items-center h-full pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={modalData.is_active}
                                            onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                            className="w-5 h-5 rounded text-primary-600"
                                        />
                                        <span className="font-medium">Active</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2 border rounded-xl hover:bg-slate-50">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Item"
                message="Are you sure you want to delete this item?"
            />
        </div>
    );
};

export default DSAPartnerProgramSupportTrainingPage;
